/* eslint-disable default-param-last */
/* eslint-disable camelcase */

import { useTranslation } from 'react-i18next';

import { getPreciseFeeCredits } from 'components/trade_fee_credits/trade_fee_info';
import {
  DELTA_CASH_DISCOUNT_PERCENT,
  IS_INDIAN_EXCHANGE,
  MARGIN_TYPES,
  OFFER_TYPES_MAPPING,
} from 'constants/constants';
import { ORDER_TYPE, PLACE_ORDER_TYPE } from 'constants/enums';
import { convertExponentialToDecimal, convertOrderBook } from 'helpers/assetUtils';
import {
  round_by_tick_size,
  calculateEstimatedMarketPrice,
  applyGST,
} from 'helpers/formulas';
import {
  getZeroFeeOfferEstimatedFee,
  isEligibleForZeroFeeOptionsOffer,
} from 'helpers/offers';
import { indexBy, isEmpty, isNil, values } from 'helpers/ramda';
import {
  cropAfterDecimals,
  discountCalculator,
  getDurationFromNow,
  isFutures,
  isKycRefreshExpired,
  isKycRequiredDeadlineCrossed,
  isOptions,
} from 'helpers/utils';
import { ContractType } from 'types/IProducts';
import Box from 'UIKit/Box';

export const disablePlaceOrderComponents = (isBuyActionState, contractType) => {
  if (isBuyActionState && isOptions(contractType)) {
    return true;
  }
  return false;
};

export const showCalculator = contractType => {
  switch (contractType) {
    case 'call_options':
    case 'put_options':
    case 'move_options':
    case 'options_combos':
    case 'turbo_put_options':
    case 'turbo_call_options':
    case 'interest_rate_swaps':
    case 'spreads':
    case 'spot':
      return false;
    default:
      return true;
  }
};

export const showLeverageSlider = (isBuyActionState, product) => {
  const { product_specs, contract_type } = product;
  switch (contract_type) {
    case 'futures':
    case 'perpetual_futures':
      return true;
    case 'call_options':
    case 'put_options':
    case 'move_options':
    case 'options_combos':
      return !isBuyActionState;
    case 'turbo_put_options':
    case 'turbo_call_options':
      return false;
    case 'interest_rate_swaps':
      if (isBuyActionState) {
        return !!Number(product_specs.floating_rate_min < 0);
      }
      return true;

    case 'spot':
      return false;
    default:
      return true;
  }
};

export const sellOrdersNotAllowed = (contractType, isBuyActionState) => {
  switch (contractType) {
    case 'turbo_put_options':
    case 'turbo_call_options':
      return !isBuyActionState;
    default:
      return false;
  }
};

export const showBracketOrderSection = (
  contractType,
  isBuyActionState,
  userData = {},
  availableBalance,
  pfThreshold
) => {
  const isKycExpired = isKycRefreshExpired(userData);
  const isKycRequired = isKycRequiredDeadlineCrossed(userData);

  // we are enabling reduce only mode if kyc_refresh is expired or kyc_required deadline is crossed
  // hence, removing bracketorder section to show a message
  if (
    isKycExpired ||
    isKycRequired ||
    (userData.margin_mode === MARGIN_TYPES.PORTFOLIO && availableBalance < pfThreshold)
  ) {
    return false;
  }

  switch (contractType) {
    case 'turbo_put_options':
    case 'turbo_call_options':
      return !!isBuyActionState;
    case 'spreads':
    case 'spot':
    case 'interest_rate_swaps':
      return false;
    default:
      return true;
  }
};

const getOrderMetricsLabel = ({ contractType = '' }) => {
  const isProductAnFuture = isFutures(contractType);
  const isProductAnOption = isOptions(contractType);
  if (isProductAnFuture) return 'notional';
  if (isProductAnOption) return 'orderSize';
  return 'orderValue';
};

const getFormattedLiquidationPrice = ({
  isProductAnOption,
  isBuyActionState,
  liquidationPrice,
  productTickSize,
  priceSymbol,
  customFormatting = null,
  marginType,
}) => {
  // when product is an option
  //  portfolio mode (long & short) and cross mode (long & short and isolated mode long,
  //  we are showing NA
  if (
    (isProductAnOption &&
      [MARGIN_TYPES.CROSS, MARGIN_TYPES.PORTFOLIO].includes(marginType)) ||
    (isProductAnOption && isBuyActionState && marginType === MARGIN_TYPES.ISOLATED)
  ) {
    return 'NA';
  }

  if (customFormatting) {
    return (
      <>
        {!liquidationPrice
          ? '-'
          : customFormatting(
              Number(round_by_tick_size(liquidationPrice, productTickSize))
            )}{' '}
        <Box as="span" textColor="secondary">
          {priceSymbol}
        </Box>
      </>
    );
  }

  return (
    <>
      {!liquidationPrice ? '-' : round_by_tick_size(liquidationPrice, productTickSize)}{' '}
      <Box as="span" textColor="secondary">
        {priceSymbol}
      </Box>
    </>
  );
};

export const showMakerTakerPopup = contractType => {
  if (contractType === ContractType.Tracker) return false;
  return true;
};

const LocalizedValue = ({ localeKey, options }) => {
  const { t } = useTranslation(['trading']);
  return t(`trading:${localeKey}`, options);
};

const calculateEstimatedTradingFee = ({
  price,
  contract_size,
  size,
  settling_asset,
  spotPrice,
  maker_commission,
  taker_commission,
  postOnly = false,
  premiumCommissionRate,
  contractType,
  estimatedExecPrice,
  orderType,
  notional_type,
  position,
  side,
  isConfirmationTable = false,
}) => {
  const isProductAFuture = isFutures(contractType);
  let priceToUseForNotional = null;
  let effectiveCommission = null;
  let sizeToUse = size;

  /**
   * This function is used in the margin component to calculate trading fees
   * for "Funds Required" and "Estimated Margin" in the confirmation pop-up.
   *
   * While calculating trading fees in these modules,
   * the position needs to be offset, which is done in the if condition.
   */

  if (!isConfirmationTable) {
    const positionSize = position ? parseFloat(position.size, 10) : 0;
    const isOpenBuy = positionSize >= 0;
    const isUserBuy = side === 'buy';
    if (isOpenBuy === isUserBuy) {
      sizeToUse = size;
    } else {
      const absPosition = Math.abs(positionSize);
      if (size <= absPosition) {
        sizeToUse = 0;
      } else {
        sizeToUse = size - absPosition;
      }
    }
  }

  // Decide the price to use for notional
  if (isProductAFuture) {
    if (orderType === PLACE_ORDER_TYPE.MARKET || orderType === ORDER_TYPE.MARKET) {
      priceToUseForNotional = estimatedExecPrice;
    } else {
      priceToUseForNotional = price;
    }
  } else {
    priceToUseForNotional = spotPrice; // Use spot price for options
  }

  // Decide the commission to use
  if (postOnly || orderType === PLACE_ORDER_TYPE.MAKER_ONLY) {
    effectiveCommission = maker_commission; // Maker commission if Maker Only order and postOnly,now renamed to maker, is checked.
  } else {
    effectiveCommission = taker_commission; // Taker commission otherwise
  }

  const feeByPremium = Number(contract_size) * Math.abs(sizeToUse) * Number(price);

  const contractPrice =
    notional_type === 'inverse' ? 1 / priceToUseForNotional : priceToUseForNotional;

  const feeByNotional =
    contract_size *
    Math.abs(sizeToUse) *
    Number(effectiveCommission) *
    convertExponentialToDecimal(contractPrice);

  let tradingFee = feeByNotional;

  const isProductAnOption = isOptions(contractType);

  if (isProductAnOption) {
    tradingFee = Math.min(feeByNotional, premiumCommissionRate * feeByPremium);
  }

  let estTradingFee = applyGST(tradingFee);
  const unformattedEstTradingFees = estTradingFee;
  const precision = estTradingFee <= 0.01 ? 4 : settling_asset?.minimum_precision;

  estTradingFee = cropAfterDecimals(
    convertExponentialToDecimal(estTradingFee),
    precision
  );

  return {
    label: (
      <LocalizedValue
        localeKey={IS_INDIAN_EXCHANGE ? 'estTradingFeeWithGST' : 'estTradingFee'}
      />
    ),
    value: (
      <>
        {estTradingFee}{' '}
        <Box as="span" textColor="secondary">
          {settling_asset?.symbol}
        </Box>
      </>
    ),
    id: 'estTradingFee',
    className: 'estimated-trading-fees',
    unformattedEstTradingFees,
  };
};

const calculateEstimatedTradingFeeAfterVIPProgramBenefits = (
  tradingFee,
  vip_discount_factor,
  feeByPremium
) => {
  let estimatedTradingFee;
  // !! check this 0.1 before using this function. currently not being used in codebase
  if (tradingFee !== 0.1 * Number(feeByPremium)) {
    estimatedTradingFee = discountCalculator(Number(vip_discount_factor), tradingFee);
  } else {
    estimatedTradingFee = tradingFee;
  }
  return estimatedTradingFee;
};

const calculateEstimatedTradingFeeAfterDeltaCashCredits = (tradingFee, balances) => {
  let estimatedTradingFee;
  let bottomText;
  const balancesBySymbol = indexBy(balance => balance.asset.symbol, values(balances));
  const [, , feeCreditsUSDT] = getPreciseFeeCredits(balancesBySymbol);

  if (Number(feeCreditsUSDT) > 0) {
    const applicableCoveragePercentage = DELTA_CASH_DISCOUNT_PERCENT / 100; // global it is 25% and India 100%
    const offset = Math.min(
      Number(applicableCoveragePercentage * tradingFee),
      feeCreditsUSDT
    );

    estimatedTradingFee = Number(tradingFee) - offset;

    bottomText = (
      <LocalizedValue
        localeKey="partialTradingFeeDeduction"
        options={{ offsetValue: cropAfterDecimals(offset, 2) }}
      />
    );
  } else {
    bottomText = null;
  }
  return { estimatedTradingFee, bottomText };
};

const calculateEstimatedTradingFeeAfterApplicableSettlementPeriod = (
  estTradingFee,
  settlement_date,
  activeOffers,
  appliedOffers,
  side,
  settling_asset
) => {
  const duration = getDurationFromNow(settlement_date);
  const isLessThan12Hours = duration.days() === 0 && duration.hours() < 12;

  // No fee will be charged if buy order is placed within 12 hours of the settlement date.
  // Will remove this when offer gets over.
  if (
    isLessThan12Hours &&
    side === 'buy' &&
    isEligibleForZeroFeeOptionsOffer(activeOffers, appliedOffers)
  ) {
    const zeroFeeOffer = activeOffers[OFFER_TYPES_MAPPING.ZERO_FEE_OPTIONS_BUYING];
    const claimedZeroFeeOffer =
      appliedOffers[OFFER_TYPES_MAPPING.ZERO_FEE_OPTIONS_BUYING];
    const feeToBeCharged = getZeroFeeOfferEstimatedFee({
      offer: zeroFeeOffer,
      claimedOffer: claimedZeroFeeOffer,
      estimatedFee: estTradingFee,
    });
    const formattedFeeToBeCharged = cropAfterDecimals(
      feeToBeCharged,
      settling_asset && settling_asset.minimum_precision
        ? settling_asset.minimum_precision
        : 2
    );

    return {
      label: <LocalizedValue localeKey="estTradingFee" />,
      strikedValue:
        Number(estTradingFee) !== 0
          ? `${cropAfterDecimals(
              estTradingFee,
              settling_asset && settling_asset.minimum_precision
                ? settling_asset.minimum_precision
                : 2
            )} ${settling_asset.symbol}`
          : null,
      value: (
        <>
          {formattedFeeToBeCharged}{' '}
          <Box as="span" textColor="secondary">
            {settling_asset.symbol}
          </Box>
        </>
      ),
      className: 'trading-fee-estimate',
      bottomText: (
        <LocalizedValue
          localeKey={feeToBeCharged > 0 ? 'estTradingFee' : 'feeNotApplicable'}
        />
      ),
    };
  }

  return undefined;
};

const calculateBandedPrice = (side, price_band) => {
  const upperLimit =
    isEmpty(price_band) || isNil(price_band) ? null : price_band.upper_limit;
  const lowerLimit =
    isEmpty(price_band) || isNil(price_band) ? null : price_band.lower_limit;

  const banded_price = side === 'buy' ? upperLimit : lowerLimit;

  return banded_price;
};

const priceToUse = (
  selectedProduct,
  side,
  orderSize,
  markPrice,
  orderBook,
  orderPrice,
  orderType,
  priceBand
) => {
  // order price is the price entered by user in the limit price input
  // mark price is the one when user clicks on best bid or best offer in the limit price input
  const limitPrice = Number(orderPrice) || markPrice;
  const orders = convertOrderBook(orderBook, side);
  // impact price can also be referred as estimated execution price
  const impactPrice = calculateEstimatedMarketPrice(
    orderSize, // kept infinity because we need price from whole orderbook . calculateEstimateMarketPrice will pick minimum of ordersize and accumulated size from orderbook
    orders,
    selectedProduct?.tick_size,
    selectedProduct?.notional_type,
    markPrice
  );
  if (
    isFutures(selectedProduct?.contract_type) &&
    (orderType === ORDER_TYPE.LIMIT || orderType === PLACE_ORDER_TYPE.LIMIT)
  ) {
    const bandPrice = calculateBandedPrice(side, priceBand);

    if (side === 'buy') {
      return Math.min(limitPrice, impactPrice, bandPrice);
    }
    if (side === 'sell') {
      return Math.max(limitPrice, impactPrice, bandPrice);
    }
  }

  if (
    isOptions(selectedProduct?.contract_type) &&
    (orderType === ORDER_TYPE.LIMIT || orderType === PLACE_ORDER_TYPE.LIMIT)
  ) {
    if (side === 'buy') {
      return Math.min(limitPrice, impactPrice);
    }
    if (side === 'sell') {
      return Math.max(limitPrice, impactPrice);
    }
  }
  if (isOptions(selectedProduct?.contract_type)) {
    return impactPrice;
  }

  return limitPrice; // if order type is limit it will return order price if order price exists else mark price and for market order it will return mark price
};

export {
  getOrderMetricsLabel,
  getFormattedLiquidationPrice,
  calculateEstimatedTradingFee,
  calculateEstimatedTradingFeeAfterVIPProgramBenefits,
  calculateEstimatedTradingFeeAfterDeltaCashCredits,
  calculateEstimatedTradingFeeAfterApplicableSettlementPeriod,
  priceToUse,
  calculateBandedPrice,
};
